import { useEffect } from 'react';
import { useLoader, useThree } from '@react-three/fiber';
import { Color, CubeTextureLoader, type CubeTexture } from 'three/webgpu';

import { COLORS } from '../core/constants';
import { CUBE_FACES, CUBE_PATH } from './assets';

/** Matches the near-black backdrop the original rendered behind everything. */
const BACKDROP = new Color(COLORS.black);

/** How much of the room cubemap reaches the materials. */
const ENVIRONMENT_INTENSITY = 0.55;

/**
 * Loads the studio cubemap and installs it as the scene environment.
 *
 * It drives reflections only — the visible backdrop stays a flat dark colour,
 * the way the original looked, so the instrument reads against black rather
 * than against a grey studio wall.
 */
export function useEnvironment(): CubeTexture {
  // `useLoader` treats the outer array as a list of inputs, so one cube map
  // request comes back as a one-element array.
  const [cubeTexture] = useLoader(CubeTextureLoader, [CUBE_FACES], (loader) => {
    (loader as CubeTextureLoader).setPath(CUBE_PATH);
  }) as unknown as [CubeTexture];

  const scene = useThree((state) => state.scene);

  useEffect(() => {
    scene.background = BACKDROP;
    scene.environment = cubeTexture;
    scene.environmentIntensity = ENVIRONMENT_INTENSITY;
    return () => {
      scene.background = null;
      scene.environment = null;
    };
  }, [scene, cubeTexture]);

  return cubeTexture;
}
